<template>
  <Card title="請選擇付款方式" subtitle="販賣機已就緒,請選擇要如何付款">
    <div class="countdown" :class="{ warn: remaining <= 10 }">
      <div class="countdown-label">請於時間內選擇</div>
      <div class="countdown-value">
        <template v-if="remaining > 0">{{ remaining }} 秒</template>
        <template v-else>已逾時,等待系統確認…</template>
      </div>
      <div class="countdown-bar">
        <div class="countdown-bar-fill" :style="{ width: percent + '%' }" />
      </div>
    </div>

    <div class="choices">
      <button
        class="choice"
        :disabled="selecting"
        @click="$emit('select', 'mpay')"
      >
        <span class="choice-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
               stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
            <path d="M2.5 9.5h19" />
            <path d="M6 14.5h4" />
          </svg>
        </span>
        <span class="choice-text">
          <span class="choice-title">電子支付</span>
          <span class="choice-desc">LINE Pay / 街口,手機輸入金額付款</span>
        </span>
      </button>

      <button
        class="choice"
        :disabled="selecting"
        @click="$emit('select', 'cash')"
      >
        <span class="choice-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
               stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="2.5" y="6.5" width="19" height="11" rx="2" />
            <circle cx="12" cy="12" r="2.4" />
            <path d="M5.6 9.4h.01M18.4 14.6h.01" />
          </svg>
        </span>
        <span class="choice-text">
          <span class="choice-title">現金支付</span>
          <span class="choice-desc">於販賣機投入現金購買</span>
        </span>
      </button>
    </div>

    <div v-if="selecting" class="selecting">
      <Spinner />
      <span>處理中…</span>
    </div>
    <p v-if="error" class="err">{{ error }}</p>

    <template #footer>
      <button class="btn btn-ghost full" :disabled="cancelling" @click="$emit('cancel')">
        取消交易
      </button>
    </template>
  </Card>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Card from './Card.vue'
import Spinner from './Spinner.vue'
import type { QrPayMethod } from '@/types/qr'

// server 逾時窗口為「建 session(createdAt)起 60 秒」,整個 WAITING_NOTIFY 共用,故由 session 起始時間起算
const DECIDE_TIMEOUT_SECONDS = 60

const props = defineProps<{
  /** 倒數起點(epoch ms):session 建立時間 */
  startedAt: number
  selecting?: boolean
  cancelling?: boolean
  error?: string
}>()
defineEmits<{
  (e: 'select', method: QrPayMethod): void
  (e: 'cancel'): void
}>()

const now = ref(Date.now())
let timer: number | null = null

const remaining = computed(() => {
  const elapsed = Math.floor((now.value - props.startedAt) / 1000)
  return Math.max(0, DECIDE_TIMEOUT_SECONDS - elapsed)
})
const percent = computed(() => (remaining.value / DECIDE_TIMEOUT_SECONDS) * 100)

function start() {
  stop()
  now.value = Date.now()
  timer = window.setInterval(() => {
    now.value = Date.now()
  }, 1000)
}
function stop() {
  if (timer != null) {
    window.clearInterval(timer)
    timer = null
  }
}

watch(() => props.startedAt, start)
onMounted(start)
onBeforeUnmount(stop)
</script>

<style scoped>
.choices {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin: 16px 0 4px;
}
.choice {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 20px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.choice:active {
  border-color: #ea580c;
  box-shadow: 0 2px 12px rgba(234, 88, 12, 0.18);
}
.choice:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.choice-icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #fff7ed;
  color: #ea580c;
  display: flex;
  align-items: center;
  justify-content: center;
}
.choice-icon svg {
  width: 24px;
  height: 24px;
}
.choice-text {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.choice-title {
  font-size: 17px;
  font-weight: 700;
  color: #111827;
}
.choice-desc {
  font-size: 13px;
  color: #6b7280;
}
.selecting {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 14px;
  color: #1e40af;
  font-size: 14px;
}
.err {
  color: #dc2626;
  font-size: 13px;
  margin: 10px 0 0;
}
.full {
  width: 100%;
}
.countdown {
  background: #fff7ed;
  border-radius: 12px;
  padding: 12px 14px;
}
.countdown-label {
  font-size: 12px;
  color: #9a3412;
}
.countdown-value {
  font-size: 22px;
  font-weight: 700;
  color: #ea580c;
  margin: 2px 0 8px;
  font-variant-numeric: tabular-nums;
}
.countdown-bar {
  height: 6px;
  background: #fed7aa;
  border-radius: 999px;
  overflow: hidden;
}
.countdown-bar-fill {
  height: 100%;
  background: #ea580c;
  border-radius: 999px;
  transition: width 1s linear;
}
.countdown.warn .countdown-value {
  color: #b91c1c;
}
.countdown.warn .countdown-bar-fill {
  background: #b91c1c;
}
</style>
